import { useCallback, useEffect } from 'react';
import type { Dispatch, SetStateAction, Key } from 'react';
import type { ColumnConfig } from './useColumnVisibility';
import { listingSettingsService, type ListingKey } from '../services/listingSettings';

type SetColumnConfig<T extends ColumnConfig> = Dispatch<SetStateAction<T[]>>;

export const useListingColumnsPersistence = <T extends ColumnConfig>(
  listingKey: ListingKey,
  baseColumns: T[],
  maxVisibleColumns: number,
  setColumnConfig: SetColumnConfig<T>
) => {
  const applyVisibleKeys = useCallback(
    (keys: Key[]) => {
      if (!keys.length) {
        return;
      }
      const allowedKeys = new Set(baseColumns.map((col) => col.key));
      const nextKeys = keys
        .filter((key): key is string => typeof key === 'string' && allowedKeys.has(key))
        .slice(0, maxVisibleColumns);
      if (!nextKeys.length) {
        return;
      }
      setColumnConfig((prev) =>
        prev.map((col) => ({
          ...col,
          visible: nextKeys.includes(col.key),
        }))
      );
    },
    [baseColumns, maxVisibleColumns, setColumnConfig]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const stored = await listingSettingsService.getVisibleColumns(listingKey);
        if (!stored || cancelled) {
          return;
        }
        applyVisibleKeys(stored);
      } catch {
        // Ignore loading errors and fall back to defaults
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [applyVisibleKeys, listingKey]);

  const persistVisibleKeys = useCallback(
    async (keys: Key[]) => {
      const filtered = keys.filter((key): key is string => typeof key === 'string');
      if (!filtered.length) {
        return;
      }
      try {
        await listingSettingsService.updateVisibleColumns(listingKey, filtered);
      } catch {
        // Ignore persistence errors
      }
    },
    [listingKey]
  );

  return { applyVisibleKeys, persistVisibleKeys };
};
